import React from 'react';
import { Info, Package } from 'lucide-react';
import type { ProductCardData } from '../../utils/productBlocks';

interface ProductCardProps extends ProductCardData {
  brandColor: string;
  isDark: boolean;
}

const ProductCard: React.FC<ProductCardProps> = ({
  title,
  price,
  image,
  variants,
  checkout_url,
  color_image_fallback,
  brandColor,
  isDark,
}) => {
  const cardBg = isDark ? '#1f2937' : '#ffffff';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const titleColor = isDark ? '#ffffff' : '#111827';
  const subColor = isDark ? '#9ca3af' : '#6b7280';
  const priceBg = isDark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)';
  const priceColor = isDark ? '#4ade80' : '#16a34a';

  return (
    <div
      style={{
        background: cardBg,
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${brandColor}`,
        borderRadius: '12px',
        overflow: 'hidden',
        margin: '8px 0',
        display: 'flex',
        flexDirection: 'row',
        gap: '0',
      }}
    >
      {/* Product image — connector cards may have a null image
          (Plan-VoiceConnectorCards). Keep the 90px column and show a muted
          Package placeholder instead of dropping the column entirely, so the
          card layout stays consistent. Same fallback pattern as OrderCard's
          line-item thumbs (lucide Package) and ProductTile's placeholder
          box (muted bg #111827/#f3f4f6). Shopify cards always carry an
          image → the has-image branch below is unchanged. */}
      {image ? (
        <div
          style={{
            width: '90px',
            minHeight: '90px',
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          <img
            src={image}
            alt={title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </div>
      ) : (
        <div
          style={{
            width: '90px',
            minHeight: '90px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isDark ? '#111827' : '#f3f4f6',
            color: isDark ? '#4b5563' : '#9ca3af',
          }}
          aria-hidden="true"
        >
          <Package size={28} />
        </div>
      )}

      {/* Content */}
      <div style={{ padding: '10px 12px', flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: '13px',
            fontWeight: 600,
            color: titleColor,
            lineHeight: '1.3',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {title}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
          {/* Connector cards (Plan-VoiceConnectorCards) may omit price — hide
              the pill rather than painting an empty green chip. Same guard
              chat's ProductTile already has; Shopify always sends price. */}
          {price && (
            <span
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: priceColor,
                background: priceBg,
                padding: '1px 8px',
                borderRadius: '6px',
              }}
            >
              {price}
            </span>
          )}
          {variants && (
            <span style={{ fontSize: '11px', color: subColor }}>
              {variants}
            </span>
          )}
        </div>

        {/* Color-fallback note — the shown image is a default/hero color, not
            the one the shopper asked for. Muted, no layout shift when absent. */}
        {color_image_fallback && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              marginTop: '4px',
              fontSize: '11px',
              color: subColor,
              lineHeight: '1.3',
            }}
          >
            <Info size={12} style={{ flexShrink: 0 }} aria-hidden="true" />
            <span>Image may show a default color</span>
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          {checkout_url && (
            <a
              href={checkout_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#ffffff',
                background: brandColor,
                padding: '4px 12px',
                borderRadius: '6px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              Buy Now &#8599;
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
